import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '/api';
const STORAGE_KEY = 'hcai_admin_token';
const DARK_KEY    = 'hcai_admin_dark';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getToken() {
  return localStorage.getItem(STORAGE_KEY);
}

function saveToken(t) {
  localStorage.setItem(STORAGE_KEY, t);
}

function clearToken() {
  localStorage.removeItem(STORAGE_KEY);
}

async function adminFetch(path, options = {}) {
  const token = getToken();
  const res = await fetch(`${API}/admin${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

function accountStatus(user, dark) {
  if (user.lockedUntil && new Date(user.lockedUntil) > new Date()) {
    return { label: 'Locked', color: dark ? 'bg-red-900/40 text-red-400' : 'bg-red-100 text-red-700' };
  }
  return { label: 'Active', color: dark ? 'bg-green-900/40 text-green-400' : 'bg-green-100 text-green-700' };
}

function subBadge(status, dark) {
  if (dark) {
    const map = {
      trial:    'bg-amber-900/40 text-amber-300',
      active:   'bg-sky-900/40 text-sky-300',
      starter:  'bg-indigo-900/40 text-indigo-300',
      past_due: 'bg-orange-900/40 text-orange-300',
      cancelled:'bg-slate-700 text-slate-400',
      expired:  'bg-slate-700 text-slate-400',
    };
    return map[status] || 'bg-slate-700 text-slate-400';
  }
  const map = {
    trial:    'bg-amber-100 text-amber-700',
    active:   'bg-sky-100 text-sky-700',
    starter:  'bg-indigo-100 text-indigo-700',
    past_due: 'bg-orange-100 text-orange-700',
    cancelled:'bg-slate-100 text-slate-500',
    expired:  'bg-slate-100 text-slate-500',
  };
  return map[status] || 'bg-slate-100 text-slate-500';
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { token } = await adminFetch('/login', {
        method: 'POST',
        body: JSON.stringify({ password }),
      });
      saveToken(token);
      onLogin();
    } catch (err) {
      setError(err.message || 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo + title */}
        <div className="text-center mb-8">
          <img src="/icon.png?v=3" alt="HealthClear AI" className="h-10 w-auto mx-auto mb-4" />
          <div className="inline-flex items-center gap-2 bg-slate-700/60 border border-slate-600 rounded-full px-3 py-1 mb-2">
            <span className="w-2 h-2 rounded-full bg-red-400 inline-block"></span>
            <span className="text-slate-300 text-xs font-medium tracking-wide uppercase">Admin Portal</span>
          </div>
          <p className="text-slate-400 text-sm mt-2">Restricted access — authorized personnel only</p>
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 shadow-2xl">
          <h1 className="text-white font-semibold text-lg mb-6">Admin sign in</h1>

          {error && (
            <div className="mb-4 px-3 py-2.5 bg-red-900/40 border border-red-700/50 rounded-lg text-sm text-red-300">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1.5">
                Admin password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
                placeholder="Enter admin password"
                className="w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg text-sm transition-colors"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Admin name store (localStorage) ─────────────────────────────────────────
const ADMIN_NAMES_KEY = 'hcai_admin_names';

function loadAdminNames() {
  try {
    const raw = localStorage.getItem(ADMIN_NAMES_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return [];
}

function saveAdminNames(names) {
  localStorage.setItem(ADMIN_NAMES_KEY, JSON.stringify(names));
}

// ─── Audit Modal ──────────────────────────────────────────────────────────────
function AuditModal({ action, userEmail, onConfirm, onCancel, dark }) {
  const [adminNames, setAdminNames] = useState(() => loadAdminNames());
  const [selectedName, setSelectedName] = useState(() => {
    const saved = loadAdminNames();
    return saved.length > 0 ? saved[0] : '__add__';
  });
  const [addingName, setAddingName] = useState(() => loadAdminNames().length === 0);
  const [newFirst, setNewFirst] = useState('');
  const [newLast, setNewLast]   = useState('');

  const [reason, setReason]       = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [newSubscription, setNewSubscription] = useState('active');

  const SUB_OPTIONS = [
    { value: 'active',    label: 'Full Access (active)' },
    { value: 'trial',     label: 'Trial' },
    { value: 'starter',   label: 'Starter' },
    { value: 'past_due',  label: 'Past Due' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'expired',   label: 'Expired' },
  ];

  const actionLabels = {
    reset:               { label: 'Reset Password',       color: dark ? 'text-amber-400' : 'text-amber-700', bg: dark ? 'bg-amber-900/30 border-amber-700/50' : 'bg-amber-50 border-amber-200' },
    lock:                { label: 'Lock Account',         color: dark ? 'text-red-400'   : 'text-red-700',   bg: dark ? 'bg-red-900/30 border-red-700/50'     : 'bg-red-50 border-red-200' },
    unlock:              { label: 'Unlock Account',       color: dark ? 'text-green-400' : 'text-green-700', bg: dark ? 'bg-green-900/30 border-green-700/50'  : 'bg-green-50 border-green-200' },
    'change-subscription': { label: 'Change Subscription', color: dark ? 'text-sky-400'   : 'text-sky-700',   bg: dark ? 'bg-sky-900/30 border-sky-700/50'      : 'bg-sky-50 border-sky-200' },
    delete:              { label: 'Delete Account',       color: dark ? 'text-red-400'   : 'text-red-700',   bg: dark ? 'bg-red-900/30 border-red-700/50'     : 'bg-red-50 border-red-200' },
    'release-org-seat':  { label: 'Release Organisation Seat', color: dark ? 'text-amber-400' : 'text-amber-700', bg: dark ? 'bg-amber-900/30 border-amber-700/50' : 'bg-amber-50 border-amber-200' },
  };
  const meta = actionLabels[action] || { label: action, color: dark ? 'text-slate-300' : 'text-slate-700', bg: dark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200' };

  const handleAddName = () => {
    const fn = newFirst.trim();
    const ln = newLast.trim();
    if (!fn || !ln) { setError('Enter both first and last name.'); return; }
    const fullName = `${fn} ${ln}`;
    if (adminNames.includes(fullName)) { setError('That name is already in the list.'); return; }
    const updated = [...adminNames, fullName];
    saveAdminNames(updated);
    setAdminNames(updated);
    setSelectedName(fullName);
    setNewFirst('');
    setNewLast('');
    setAddingName(false);
    setError('');
  };

  const handleRemoveName = (name) => {
    const updated = adminNames.filter(n => n !== name);
    saveAdminNames(updated);
    setAdminNames(updated);
    if (selectedName === name) {
      setSelectedName(updated.length > 0 ? updated[0] : '__add__');
      if (updated.length === 0) setAddingName(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const rs = reason.trim();
    if (addingName) { setError('Save the admin name before continuing.'); return; }
    if (!selectedName || selectedName === '__add__') { setError('Select an admin name.'); return; }
    if (!rs) { setError('Reason is required.'); return; }
    if (rs.length < 10) { setError('Please provide a more detailed reason (at least 10 characters).'); return; }
    if (action === 'change-subscription' && !newSubscription) { setError('Please select a subscription level.'); return; }
    if (action === 'delete' && confirmEmail.trim() !== userEmail) { setError('Email does not match. Please type the exact email to confirm.'); return; }
    const [fn, ...rest] = selectedName.split(' ');
    const ln = rest.join(' ');
    setError('');
    setSubmitting(true);
    try {
      await onConfirm({ firstName: fn, lastName: ln, reason: rs, subscription: newSubscription });
    } catch (err) {
      setError(err.message || 'Action failed.');
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className={`rounded-2xl shadow-2xl w-full max-w-md p-6 border ${dark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        {/* Header */}
        <div className={`flex items-center gap-3 mb-5 px-4 py-3 rounded-xl border ${meta.bg}`}>
          <svg className={`w-5 h-5 shrink-0 ${meta.color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className={`text-sm font-semibold ${meta.color}`}>{meta.label}</p>
            <p className={`text-xs mt-0.5 break-all ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{userEmail}</p>
          </div>
        </div>

        <p className={`text-sm mb-4 ${dark ? 'text-slate-300' : 'text-slate-600'}`}>
          Select the admin performing this action and provide a reason. This will be logged for audit purposes.
        </p>

        {error && (
          <div className={`mb-4 px-3 py-2 rounded-lg text-sm border ${dark ? 'bg-red-900/40 border-red-700/50 text-red-400' : 'bg-red-50 border-red-200 text-red-700'}`}>{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Admin name selector */}
          <div>
            <label className={`block text-xs font-medium mb-1 ${dark ? 'text-slate-400' : 'text-slate-600'}`}>Admin performing action <span className="text-red-500">*</span></label>
            {!addingName ? (
              <div className="flex gap-2">
                <select
                  value={selectedName}
                  onChange={e => {
                    if (e.target.value === '__add__') { setAddingName(true); }
                    else setSelectedName(e.target.value);
                  }}
                  autoFocus
                  className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 ${dark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-200'}`}
                >
                  {adminNames.map(n => <option key={n} value={n}>{n}</option>)}
                  <option value="__add__">+ Add new name…</option>
                </select>
                {adminNames.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveName(selectedName)}
                    title="Remove this name"
                    className={`px-2.5 py-2 rounded-lg text-xs font-medium transition-colors ${dark ? 'bg-slate-700 hover:bg-red-900/40 text-slate-400 hover:text-red-400' : 'bg-slate-100 hover:bg-red-50 text-slate-500 hover:text-red-600'}`}
                  >
                    ✕
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={newFirst}
                    onChange={e => setNewFirst(e.target.value)}
                    autoFocus
                    placeholder="First name"
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 ${dark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500' : 'border-slate-200'}`}
                  />
                  <input
                    type="text"
                    value={newLast}
                    onChange={e => setNewLast(e.target.value)}
                    placeholder="Last name"
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 ${dark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500' : 'border-slate-200'}`}
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleAddName}
                    className="flex-1 px-3 py-2 text-xs font-semibold bg-sky-600 hover:bg-sky-500 text-white rounded-lg transition-colors"
                  >
                    Save name
                  </button>
                  {adminNames.length > 0 && (
                    <button
                      type="button"
                      onClick={() => { setAddingName(false); setNewFirst(''); setNewLast(''); setError(''); }}
                      className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${dark ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${dark ? 'text-slate-400' : 'text-slate-600'}`}>Reason for change <span className="text-red-500">*</span></label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              required
              rows={3}
              placeholder="Describe why you are making this change…"
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none ${dark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500' : 'border-slate-200'}`}
            />
            <p className="text-xs text-slate-400 mt-1">{reason.trim().length} / 10 min characters</p>
          </div>

          {action === 'change-subscription' && (
            <div>
              <label className={`block text-xs font-medium mb-1 ${dark ? 'text-slate-400' : 'text-slate-600'}`}>New subscription level <span className="text-red-500">*</span></label>
              <select
                value={newSubscription}
                onChange={e => setNewSubscription(e.target.value)}
                required
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 ${dark ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-200'}`}
              >
                {SUB_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          )}

          {action === 'delete' && (
            <div>
              <label className={`block text-xs font-medium mb-1 ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
                Type <span className="font-mono font-bold">{userEmail}</span> to confirm permanent deletion <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={confirmEmail}
                onChange={e => setConfirmEmail(e.target.value)}
                required
                placeholder={userEmail}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${dark ? 'bg-slate-700 border-red-700/50 text-white placeholder-slate-500' : 'border-red-200'}`}
              />
              <p className={`text-xs mt-1 ${dark ? 'text-red-400' : 'text-red-600'}`}>⚠ This will permanently delete the account and all associated data. This cannot be undone.</p>
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 ${dark ? 'text-slate-300 bg-slate-700 hover:bg-slate-600' : 'text-slate-600 bg-slate-100 hover:bg-slate-200'}`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className={`flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-colors disabled:opacity-50 ${
                action === 'lock' || action === 'delete' ? 'bg-red-600 hover:bg-red-500' :
                action === 'unlock' ? 'bg-green-600 hover:bg-green-500' :
                'bg-sky-600 hover:bg-sky-500'
              }`}
            >
              {submitting ? 'Processing…' : action === 'delete' ? 'Delete permanently' : 'Confirm'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── User Row ─────────────────────────────────────────────────────────────────
function UserRow({ user, onAction, dark }) {
  const [loading, setLoading] = useState(null);
  const [tempPw, setTempPw] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'reset' | 'lock' | 'unlock'
  const status = accountStatus(user, dark);
  const isLocked = status.label === 'Locked';

  // An expired grant is kept rather than cleared, so support can see that access
  // existed and when it ended. Surfaced as "lapsed" instead of silence.
  const orgGrant = user.orgEntitlementTier ? {
    tier: user.orgEntitlementTier,
    expiresAt: user.orgEntitlementExpiresAt,
    expired: !!user.orgEntitlementExpiresAt && new Date(user.orgEntitlementExpiresAt) <= new Date(),
    org: user.codeRedemption?.code?.organization?.name || 'an organisation',
  } : null;
  const linkedToOrg = !!(user.orgEntitlementTier || user.orgDiscountCouponId || user.codeRedemption);

  const handleAuditConfirm = async ({ firstName, lastName, reason, subscription }) => {
    const audit = { adminFirstName: firstName, adminLastName: lastName, reason };
    setLoading(pendingAction);
    setPendingAction(null);
    try {
      if (pendingAction === 'reset') {
        const { tempPassword } = await adminFetch(`/users/${user.id}/reset-password`, {
          method: 'POST',
          body: JSON.stringify(audit),
        });
        setTempPw(tempPassword);
      } else if (pendingAction === 'lock') {
        await adminFetch(`/users/${user.id}/lock`, {
          method: 'POST',
          body: JSON.stringify(audit),
        });
        onAction();
      } else if (pendingAction === 'unlock') {
        await adminFetch(`/users/${user.id}/unlock`, {
          method: 'POST',
          body: JSON.stringify(audit),
        });
        onAction();
      } else if (pendingAction === 'change-subscription') {
        await adminFetch(`/users/${user.id}/subscription`, {
          method: 'POST',
          body: JSON.stringify({ ...audit, subscriptionStatus: subscription }),
        });
        onAction();
      } else if (pendingAction === 'release-org-seat') {
        await adminFetch(`/users/${user.id}/release-org-seat`, {
          method: 'POST',
          body: JSON.stringify(audit),
        });
        onAction();
      } else if (pendingAction === 'delete') {
        await adminFetch(`/users/${user.id}`, {
          method: 'DELETE',
          body: JSON.stringify(audit),
        });
        onAction();
      }
    } catch (err) {
      setLoading(null);
      throw err; // let AuditModal show the error
    }
    setLoading(null);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(tempPw).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <>
      {pendingAction && (
        <AuditModal
          action={pendingAction}
          userEmail={user.email}
          onConfirm={handleAuditConfirm}
          onCancel={() => setPendingAction(null)}
          dark={dark}
        />
      )}
      <tr className={`border-t transition-colors ${dark ? 'border-slate-700 hover:bg-slate-700/40' : 'border-slate-100 hover:bg-slate-50'}`}>
        <td className="px-4 py-3">
          <div className={`font-medium text-sm ${dark ? 'text-slate-100' : 'text-slate-800'}`}>{user.name}</div>
          <div className="text-xs text-slate-400 mt-0.5">{user.email}</div>
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${subBadge(user.subscriptionStatus, dark)}`}>
            {user.subscriptionStatus}
          </span>
          {/* An org grant lifts access without touching subscriptionStatus, so
              this column read "trial" beside someone with full access and no way
              to see why. Shown next to it rather than folded into it: they are
              two independent facts, and support needs to know which one is
              carrying the person. */}
          {orgGrant && (
            <div className={`mt-1 text-xs ${orgGrant.expired ? 'text-amber-600' : dark ? 'text-emerald-400' : 'text-emerald-700'}`}>
              {orgGrant.expired ? 'lapsed' : orgGrant.tier === 'active' ? 'full access' : 'basic'}
              {' via '}{orgGrant.org}
              {orgGrant.expiresAt && (
                <span className={dark ? 'text-slate-500' : 'text-slate-400'}>
                  {orgGrant.expired ? ' — ended ' : ' — until '}
                  {new Date(orgGrant.expiresAt).toLocaleDateString()}
                </span>
              )}
            </div>
          )}
          {!orgGrant && user.codeRedemption && (
            <div className={`mt-1 text-xs ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
              attributed to {user.codeRedemption.code?.organization?.name}
            </div>
          )}
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
            {status.label}
          </span>
        </td>
        <td className={`px-4 py-3 text-sm text-center ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
          {user._count?.labReports ?? 0}
        </td>
        <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">
          {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </td>
        <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">
          {user.lastLoginAt
            ? new Date(user.lastLoginAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/New_York', timeZoneName: 'short' })
            : <span className={dark ? 'text-slate-600' : 'text-slate-300'}>Never</span>}
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {/* Reset password */}
            <button
              onClick={() => setPendingAction('reset')}
              disabled={loading !== null}
              className={`text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${dark ? 'bg-slate-700 hover:bg-slate-600 text-slate-200' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
            >
              {loading === 'reset' ? '…' : 'Reset PW'}
            </button>

            {/* Change Subscription */}
            <button
              onClick={() => setPendingAction('change-subscription')}
              disabled={loading !== null}
              className={`text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${dark ? 'bg-sky-900/40 hover:bg-sky-900/60 text-sky-400' : 'bg-sky-50 hover:bg-sky-100 text-sky-700'}`}
            >
              {loading === 'change-subscription' ? '…' : 'Change Sub'}
            </button>

            {/* Lock / Unlock */}
            {isLocked ? (
              <button
                onClick={() => setPendingAction('unlock')}
                disabled={loading !== null}
                className={`text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${dark ? 'bg-green-900/40 hover:bg-green-900/60 text-green-400' : 'bg-green-50 hover:bg-green-100 text-green-700'}`}
              >
                {loading === 'unlock' ? '…' : 'Unlock'}
              </button>
            ) : (
              <button
                onClick={() => setPendingAction('lock')}
                disabled={loading !== null}
                className={`text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${dark ? 'bg-red-900/40 hover:bg-red-900/60 text-red-400' : 'bg-red-50 hover:bg-red-100 text-red-700'}`}
              >
                {loading === 'lock' ? '…' : 'Lock'}
              </button>
            )}

            {/* Release an organisation seat — only shown when one exists */}
            {linkedToOrg && (
              <button
                onClick={() => setPendingAction('release-org-seat')}
                disabled={loading !== null}
                title="End this person's link to their organisation: clears the grant, frees the seat, and stops the facility being billed for it"
                className={`text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${dark ? 'bg-amber-900/40 hover:bg-amber-900/60 text-amber-400' : 'bg-amber-50 hover:bg-amber-100 text-amber-700'}`}
              >
                {loading === 'release-org-seat' ? '…' : 'Release seat'}
              </button>
            )}

            {/* Delete account */}
            <button
              onClick={() => setPendingAction('delete')}
              disabled={loading !== null}
              title="Permanently delete this user and all their data"
              className={`text-xs px-2.5 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${dark ? 'bg-red-950/60 hover:bg-red-900/80 text-red-400 border border-red-800/50' : 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200'}`}
            >
              {loading === 'delete' ? '…' : 'Delete'}
            </button>
          </div>
        </td>
      </tr>

      {/* Temp password reveal row */}
      {tempPw && (
        <tr className={`border-t ${dark ? 'bg-amber-900/20 border-amber-800/40' : 'bg-amber-50 border-amber-100'}`}>
          <td colSpan={7} className="px-4 py-3">
            <div className="flex items-center gap-3 flex-wrap">
              <svg className="w-4 h-4 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className={`text-sm font-medium ${dark ? 'text-amber-300' : 'text-amber-800'}`}>Temporary password for {user.email}:</span>
              <code className={`rounded px-2 py-0.5 text-sm font-mono select-all border ${dark ? 'bg-slate-800 border-amber-700/50 text-amber-300' : 'bg-white border-amber-200 text-amber-900'}`}>
                {tempPw}
              </code>
              <button
                onClick={handleCopy}
                className={`text-xs px-2 py-1 rounded font-medium transition-colors ${dark ? 'bg-amber-900/40 hover:bg-amber-900/60 text-amber-300' : 'bg-amber-100 hover:bg-amber-200 text-amber-800'}`}
              >
                {copied ? '✓ Copied' : 'Copy'}
              </button>
              <button
                onClick={() => setTempPw(null)}
                className={`text-xs ml-auto ${dark ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-800'}`}
              >
                Dismiss
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Active Users Panel ───────────────────────────────────────────────────────
function ActiveUsersPanel({ dark }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await adminFetch('/active-users');
      setSessions(data);
      setLastUpdated(new Date());
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 30_000);
    return () => clearInterval(interval);
  }, [load]);

  const statusOf = (lastSeen) => {
    const ago = Date.now() - new Date(lastSeen).getTime();
    if (ago < 2 * 60 * 1000) return { label: 'Online', dotColor: 'bg-green-500', textColor: dark ? 'text-green-400' : 'text-green-600' };
    return { label: 'Recent', dotColor: 'bg-amber-400', textColor: dark ? 'text-amber-400' : 'text-amber-600' };
  };

  const timeAgo = (iso) => {
    const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (secs < 10) return 'just now';
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    return `${mins}m ago`;
  };

  return (
    <div className={`border rounded-xl ${dark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      {/* Header */}
      <div className={`p-4 border-b flex items-center justify-between ${dark ? 'border-slate-700' : 'border-slate-100'}`}>
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
          </span>
          <span className={`text-sm font-medium ${dark ? 'text-slate-200' : 'text-slate-700'}`}>
            {sessions.length} active session{sessions.length !== 1 ? 's' : ''}
          </span>
          {lastUpdated && (
            <span className="text-xs text-slate-400 hidden sm:inline">
              — refreshed at {lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </div>
        <button
          onClick={load}
          className={`text-sm px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-1.5 ${dark ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {error && <div className="p-6 text-center text-red-500 text-sm">{error}</div>}

      {loading ? (
        <div className="p-12 text-center text-slate-400 text-sm">Loading active sessions…</div>
      ) : sessions.length === 0 ? (
        <div className="p-12 text-center">
          <div className="text-3xl mb-3">😴</div>
          <p className={`text-sm ${dark ? 'text-slate-400' : 'text-slate-500'}`}>No users active in the last 5 minutes.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className={dark ? 'bg-slate-700/50' : 'bg-slate-50'}>
                {['User', 'Current Screen', 'Status', 'Last Seen (ET)', 'Last Seen'].map(h => (
                  <th key={h} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-left ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sessions.map(s => {
                const status = statusOf(s.lastSeen);
                return (
                  <tr key={s.userId} className={`border-t transition-colors ${dark ? 'border-slate-700 hover:bg-slate-700/40' : 'border-slate-100 hover:bg-slate-50'}`}>
                    <td className="px-4 py-3">
                      <div className={`font-medium text-sm ${dark ? 'text-slate-100' : 'text-slate-800'}`}>{s.name}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{s.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-sm ${dark ? 'text-slate-200' : 'text-slate-700'}`}>
                        <span className="text-base">📍</span>
                        {s.screen}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${status.textColor}`}>
                        <span className={`w-1.5 h-1.5 rounded-full inline-block ${status.dotColor}`}></span>
                        {status.label}
                      </span>
                    </td>
                    <td className={`px-4 py-3 text-xs whitespace-nowrap ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {new Date(s.lastSeen).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'America/New_York', timeZoneName: 'short' })}
                    </td>
                    <td className={`px-4 py-3 text-xs ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {timeAgo(s.lastSeen)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && (
        <div className={`px-4 py-2.5 border-t text-xs text-slate-400 ${dark ? 'border-slate-700' : 'border-slate-100'}`}>
          Auto-refreshes every 30 seconds · Sessions expire after 5 minutes of inactivity
        </div>
      )}
    </div>
  );
}

// ─── Audit Log Panel ──────────────────────────────────────────────────────────
const ACTION_META = {
  'reset-password':      { label: 'Reset PW',      color: (dark) => dark ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700' },
  'lock':                { label: 'Lock',           color: (dark) => dark ? 'bg-red-900/40 text-red-400'    : 'bg-red-100 text-red-700' },
  'unlock':              { label: 'Unlock',         color: (dark) => dark ? 'bg-green-900/40 text-green-400': 'bg-green-100 text-green-700' },
  'change-subscription': { label: 'Change Sub',     color: (dark) => dark ? 'bg-sky-900/40 text-sky-400'    : 'bg-sky-100 text-sky-700' },
  'delete-account':      { label: 'Delete',         color: (dark) => dark ? 'bg-red-950/60 text-red-400'    : 'bg-red-100 text-red-700' },
};

function AuditLogPanel({ dark }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const loadLogs = useCallback(async (s, a, off) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: off });
      if (s) params.set('search', s);
      if (a !== 'all') params.set('action', a);
      const data = await adminFetch(`/audit?${params}`);
      setLogs(data.logs);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLogs(search, filterAction, offset); }, [loadLogs, search, filterAction, offset]);

  const handleSearch = (v) => { setSearch(v); setOffset(0); };
  const handleFilter = (v) => { setFilterAction(v); setOffset(0); };

  return (
    <div className={`border rounded-xl ${dark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
      {/* Toolbar */}
      <div className={`p-4 border-b flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between ${dark ? 'border-slate-700' : 'border-slate-100'}`}>
        <div className="relative flex-1 max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search admin, user, or reason…"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 ${dark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500' : 'border-slate-200'}`}
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterAction}
            onChange={e => handleFilter(e.target.value)}
            className={`text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 ${dark ? 'bg-slate-700 border-slate-600 text-slate-200' : 'border-slate-200 text-slate-700'}`}
          >
            <option value="all">All actions</option>
            <option value="reset-password">Reset PW</option>
            <option value="lock">Lock</option>
            <option value="unlock">Unlock</option>
            <option value="change-subscription">Change Sub</option>
          </select>
          <button
            onClick={() => loadLogs(search, filterAction, offset)}
            className={`text-sm px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-1.5 ${dark ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      {error && <div className="p-6 text-center text-red-500 text-sm">{error}</div>}
      {loading ? (
        <div className="p-12 text-center text-slate-400 text-sm">Loading audit log…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className={dark ? 'bg-slate-700/50' : 'bg-slate-50'}>
                {['When', 'Admin', 'Action', 'User', 'Detail', 'Reason'].map(h => (
                  <th key={h} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-left ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400">No audit entries found.</td></tr>
              ) : logs.map(log => {
                const meta = ACTION_META[log.action] || { label: log.action, color: () => dark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600' };
                return (
                  <tr key={log.id} className={`border-t ${dark ? 'border-slate-700 hover:bg-slate-700/40' : 'border-slate-100 hover:bg-slate-50'}`}>
                    <td className={`px-4 py-3 text-xs whitespace-nowrap ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {new Date(log.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className={`px-4 py-3 text-sm font-medium ${dark ? 'text-slate-200' : 'text-slate-800'}`}>{log.adminName}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${meta.color(dark)}`}>{meta.label}</span>
                    </td>
                    <td className={`px-4 py-3 text-sm ${dark ? 'text-slate-300' : 'text-slate-600'}`}>{log.targetEmail}</td>
                    <td className={`px-4 py-3 text-sm ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{log.detail || <span className={dark ? 'text-slate-600' : 'text-slate-300'}>—</span>}</td>
                    <td className={`px-4 py-3 text-sm max-w-xs truncate ${dark ? 'text-slate-400' : 'text-slate-500'}`} title={log.reason}>{log.reason}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && total > 0 && (
        <div className={`px-4 py-3 border-t flex items-center justify-between text-xs ${dark ? 'border-slate-700 text-slate-400' : 'border-slate-100 text-slate-400'}`}>
          <span>Showing {offset + 1}–{Math.min(offset + LIMIT, total)} of {total} entries</span>
          <div className="flex gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - LIMIT))}
              className={`px-3 py-1 rounded text-xs font-medium disabled:opacity-40 ${dark ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
            >Previous</button>
            <button
              disabled={offset + LIMIT >= total}
              onClick={() => setOffset(offset + LIMIT)}
              className={`px-3 py-1 rounded text-xs font-medium disabled:opacity-40 ${dark ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
            >Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function AdminDashboard({ onLogout }) {
  const [tab, setTab] = useState('users'); // 'users' | 'active' | 'audit'
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dark, setDark] = useState(() => localStorage.getItem(DARK_KEY) === '1');
  const [sortCol, setSortCol] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const toggleDark = () => {
    setDark(d => {
      const next = !d;
      localStorage.setItem(DARK_KEY, next ? '1' : '0');
      return next;
    });
  };

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminFetch('/users');
      setUsers(data);
    } catch (err) {
      if (err.message.includes('Unauthorized') || err.message.includes('Forbidden') || err.message.includes('expired')) {
        clearToken();
        onLogout();
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [onLogout]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filtered = users
    .filter((u) => {
      const q = search.toLowerCase();
      const matchesSearch = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
      const isLocked = u.lockedUntil && new Date(u.lockedUntil) > new Date();
      const matchesStatus = filterStatus === 'all'
        || (filterStatus === 'locked' ? isLocked : u.subscriptionStatus === filterStatus);
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let av, bv;
      if (sortCol === 'name') { av = a.name.toLowerCase(); bv = b.name.toLowerCase(); }
      else if (sortCol === 'subscriptionStatus') { av = a.subscriptionStatus; bv = b.subscriptionStatus; }
      else if (sortCol === 'reports') { av = a._count?.labReports ?? 0; bv = b._count?.labReports ?? 0; }
      else if (sortCol === 'createdAt') { av = new Date(a.createdAt); bv = new Date(b.createdAt); }
      else if (sortCol === 'lastLoginAt') { av = a.lastLoginAt ? new Date(a.lastLoginAt) : new Date(0); bv = b.lastLoginAt ? new Date(b.lastLoginAt) : new Date(0); }
      else { return 0; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  // Stats
  const stats = {
    total: users.length,
    active: users.filter(u => u.subscriptionStatus === 'active').length,
    trial: users.filter(u => u.subscriptionStatus === 'trial').length,
    starter: users.filter(u => u.subscriptionStatus === 'starter').length,
    locked: users.filter(u => u.lockedUntil && new Date(u.lockedUntil) > new Date()).length,
  };

  return (
    <div className={`min-h-screen ${dark ? 'bg-slate-900' : 'bg-slate-50'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-10 border-b ${dark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icon.png?v=3" alt="HealthClear AI" className="h-7 w-auto" />
            <div className={`h-5 w-px ${dark ? 'bg-slate-600' : 'bg-slate-200'}`} />
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-400 inline-block"></span>
              <span className={`text-xs font-semibold tracking-wide uppercase ${dark ? 'text-slate-300' : 'text-slate-700'}`}>Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Dark mode toggle */}
            <button
              onClick={toggleDark}
              title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
              className={`p-1.5 rounded-lg transition-colors ${dark ? 'text-slate-400 hover:text-yellow-300 hover:bg-slate-700' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
            >
              {dark ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707M12 7a5 5 0 100 10A5 5 0 0012 7z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            <button
              onClick={() => { clearToken(); onLogout(); }}
              className={`text-sm flex items-center gap-1.5 transition-colors ${dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className={`text-xl font-bold ${dark ? 'text-white' : 'text-slate-900'}`}>
              {tab === 'users' ? 'User Management' : tab === 'active' ? 'Active Users' : tab === 'orgs' ? 'Facilities' : tab === 'reports' ? 'Reports' : 'Audit Log'}
            </h1>
            <p className={`text-sm mt-0.5 ${dark ? 'text-slate-400' : 'text-slate-500'}`}>
              {tab === 'users' ? 'View and manage all user accounts' : tab === 'active' ? 'Users currently active in the app — real time' : tab === 'orgs' ? 'Partner facilities, their codes and seats in use' : tab === 'reports' ? 'What people have told us is wrong' : 'Complete history of admin actions'}
            </p>
          </div>
          {/* Tab switcher */}
          <div className={`flex rounded-xl p-1 gap-1 ${dark ? 'bg-slate-700' : 'bg-slate-100'}`}>
            {[{ id: 'users', label: 'Users' }, { id: 'active', label: '🟢 Active Users' }, { id: 'orgs', label: 'Facilities' }, { id: 'reports', label: 'Reports' }, { id: 'audit', label: 'Audit Log' }].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  tab === t.id
                    ? dark ? 'bg-slate-600 text-white shadow' : 'bg-white text-slate-900 shadow'
                    : dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'
                }`}
              >{t.label}</button>
            ))}
          </div>
        </div>

        {tab === 'orgs' && <OrganizationsPanel dark={dark} onLogout={onLogout} />}

        {tab === 'reports' && <ProblemReportsPanel dark={dark} />}
        {tab === 'audit' && <AuditLogPanel dark={dark} />}

        {tab === 'active' && <ActiveUsersPanel dark={dark} />}

        {tab === 'users' && (<>
        {/* Stats cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Total users', value: stats.total, color: dark ? 'text-slate-200' : 'text-slate-700', filter: 'all' },
            { label: 'Full access', value: stats.active, color: 'text-sky-500', filter: 'active' },
            { label: 'Trial', value: stats.trial, color: 'text-amber-500', filter: 'trial' },
            { label: 'Starter', value: stats.starter, color: 'text-indigo-400', filter: 'starter' },
            { label: 'Locked', value: stats.locked, color: 'text-red-500', filter: 'locked' },
          ].map(({ label, value, color, filter }) => (
            <button
              key={label}
              onClick={() => setFilterStatus(filter)}
              className={`border rounded-xl p-4 text-left transition-all ${
                filterStatus === filter
                  ? dark ? 'bg-slate-700 border-sky-500 ring-1 ring-sky-500' : 'bg-sky-50 border-sky-400 ring-1 ring-sky-400'
                  : dark ? 'bg-slate-800 border-slate-700 hover:border-slate-500' : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className={`text-2xl font-bold ${color}`}>{value}</div>
              <div className={`text-xs mt-0.5 ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{label}</div>
            </button>
          ))}
        </div>

        {/* Filters + Table */}
        <div className={`border rounded-xl ${dark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className={`p-4 border-b flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between ${dark ? 'border-slate-700' : 'border-slate-100'}`}>
            <div className="relative flex-1 max-w-sm">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search by name or email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent ${dark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-500' : 'border-slate-200'}`}
              />
            </div>
            <div className="flex items-center gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className={`text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-sky-500 ${dark ? 'bg-slate-700 border-slate-600 text-slate-200' : 'border-slate-200 text-slate-700'}`}
              >
                <option value="all">All statuses</option>
                <option value="active">Full access</option>
                <option value="trial">Trial</option>
                <option value="starter">Starter</option>
                <option value="past_due">Past due</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>
              <button
                onClick={loadUsers}
                className={`text-sm px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-1.5 ${dark ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>

          {/* Table */}
          {error && (
            <div className="p-6 text-center text-red-500 text-sm">{error}</div>
          )}

          {/* The two org lines under a subscription badge mean different things,
              and the difference decides what an admin should do. Attribution is
              a marketing fact and changes nothing the person can see; a grant is
              access they actually hold. Reading one as the other leads to
              "upgrading" someone who already has access, or assuming a partner
              is covering someone who is paying for themselves. */}
          {!loading && (
            <div className={`px-4 py-3 border-b text-xs flex flex-wrap items-center gap-x-5 gap-y-2 ${
              dark ? 'border-slate-700 bg-slate-800/40 text-slate-400' : 'border-slate-100 bg-slate-50/60 text-slate-500'}`}>
              <span className="font-semibold uppercase tracking-wide">Organisation lines</span>
              <span className="flex items-center gap-1.5">
                <span className={dark ? 'text-emerald-400' : 'text-emerald-700'}>full access via …</span>
                holds access from that facility
              </span>
              <span className="flex items-center gap-1.5">
                <span className={dark ? 'text-slate-500' : 'text-slate-400'}>attributed to …</span>
                signup credited only — access unchanged
              </span>
              <span className="flex items-center gap-1.5">
                <span className="text-amber-600">lapsed</span>
                grant ended; row kept on purpose
              </span>
              <span className={`w-full ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                The Subscription badge is always the person's own billing. An organisation never changes it —
                access is the higher of the two, resolved per request.
              </span>
            </div>
          )}

          {loading ? (
            <div className="p-12 text-center text-slate-400 text-sm">Loading users…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className={dark ? 'bg-slate-700/50' : 'bg-slate-50'}>
                    {[
                      { label: 'User',         col: 'name',               align: 'left' },
                      { label: 'Subscription', col: 'subscriptionStatus', align: 'left' },
                      { label: 'Account',      col: null,                 align: 'left' },
                      { label: 'Reports',      col: 'reports',            align: 'center' },
                      { label: 'Joined',       col: 'createdAt',          align: 'left' },
                      { label: 'Last login',   col: 'lastLoginAt',        align: 'left' },
                      { label: 'Actions',      col: null,                 align: 'left' },
                    ].map(({ label, col, align }) => (
                      <th
                        key={label}
                        onClick={col ? () => handleSort(col) : undefined}
                        className={`px-4 py-3 text-xs font-semibold uppercase tracking-wide text-${align} ${
                          col ? 'cursor-pointer select-none' : ''
                        } ${dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {label}
                          {col && (
                            <span className={sortCol === col ? (dark ? 'text-sky-400' : 'text-sky-600') : 'opacity-30'}>
                              {sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                            </span>
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-400">
                        {search || filterStatus !== 'all' ? 'No users match your filters.' : 'No users found.'}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((u) => (
                      <UserRow key={u.id} user={u} onAction={loadUsers} dark={dark} />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className={`px-4 py-3 border-t text-xs text-slate-400 ${dark ? 'border-slate-700' : 'border-slate-100'}`}>
              Showing {filtered.length} of {users.length} user{users.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
        </>)}
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

// ─── Organisations ───────────────────────────────────────────────────────────
//
// Facilities, their codes, and seats in use. Note what is absent: no patient
// list, no way to look up who redeemed anything. Managing a contract does not
// require knowing whose results are whose, and the panel is built so it cannot
// drift into that.

/**
 * What people have told us is wrong.
 *
 * Staff-facing only. The message is free text somebody typed, so it is never
 * surfaced to an organisation whatever it turns out to contain — the same rule
 * the whole org side is built around.
 */
function ProblemReportsPanel({ dark }) {
  const [data, setData] = useState({ reports: [], counts: {} });
  const [filter, setFilter] = useState('new');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (status) => {
    setLoading(true); setError('');
    try { setData(await adminFetch(`/problem-reports?status=${status}`)); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(filter); }, [load, filter]);

  const setStatus = async (id, status) => {
    const who = status === 'closed'
      ? window.prompt('Your first and last name, for the record') : null;
    if (status === 'closed' && !who?.trim()) return;
    const [first, ...rest] = (who || '').trim().split(' ');
    try {
      await adminFetch(`/problem-reports/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status, adminFirstName: first, adminLastName: rest.join(' ') || '-' }),
      });
      load(filter);
    } catch (err) { setError(err.message); }
  };

  const card = dark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const label = dark ? 'text-slate-400' : 'text-slate-500';
  const value = dark ? 'text-slate-100' : 'text-slate-800';

  const CATEGORY = {
    bug: ['Broken', 'bg-rose-50 text-rose-700'],
    confusing: ['Confusing', 'bg-amber-50 text-amber-700'],
    suggestion: ['Suggestion', 'bg-sky-50 text-sky-700'],
    other: ['Other', 'bg-slate-100 text-slate-600'],
  };

  return (
    <div>
      {error && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm">{error}</div>}

      <div className="flex flex-wrap gap-2 mb-4">
        {['new', 'open', 'closed', 'all'].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${
              filter === f
                ? 'bg-cyan-600 border-cyan-600 text-white'
                : dark ? 'border-slate-600 text-slate-300' : 'border-slate-300 text-slate-600'}`}>
            {f[0].toUpperCase() + f.slice(1)}
            {data.counts?.[f] !== undefined && ` (${data.counts[f]})`}
          </button>
        ))}
      </div>

      {loading ? <p className={`text-sm ${label}`}>Loading…</p>
        : data.reports.length === 0 ? (
          <p className={`text-sm ${label}`}>
            Nothing here. {filter === 'new' ? 'No unread reports.' : `No ${filter} reports.`}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {data.reports.map((r) => {
              const [catLabel, catClass] = CATEGORY[r.category] || CATEGORY.other;
              return (
                <div key={r.id} className={`border rounded-xl p-4 ${card}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${catClass}`}>{catLabel}</span>
                    <span className={`text-xs ${label}`}>
                      {new Date(r.createdAt).toLocaleString()}
                    </span>
                    {r.status !== 'new' && (
                      <span className={`text-xs ${label}`}>· {r.status}</span>
                    )}
                    <div className="ml-auto flex gap-2">
                      {r.status !== 'open' && (
                        <button onClick={() => setStatus(r.id, 'open')}
                          className="text-xs font-semibold text-sky-600 hover:underline">Working on it</button>
                      )}
                      {r.status !== 'closed' && (
                        <button onClick={() => setStatus(r.id, 'closed')}
                          className="text-xs font-semibold text-emerald-600 hover:underline">Close</button>
                      )}
                    </div>
                  </div>

                  <p className={`text-sm whitespace-pre-wrap ${value}`}>{r.message}</p>

                  <div className={`mt-3 pt-3 border-t text-xs flex flex-wrap gap-x-4 gap-y-1 ${
                    dark ? 'border-slate-700 text-slate-400' : 'border-slate-100 text-slate-500'}`}>
                    <span>{r.user ? `${r.user.name || 'no name'} · ${r.user.email}` : 'not signed in'}</span>
                    {r.context?.page && <span>page {r.context.page}</span>}
                    {r.context?.build && <span>build {r.context.build}</span>}
                    {r.context?.userAgent && (
                      <span className="truncate max-w-md" title={r.context.userAgent}>
                        {r.context.userAgent}
                      </span>
                    )}
                    {r.handledBy && <span>closed by {r.handledBy}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

function OrganizationsPanel({ dark, onLogout }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '', type: 'urgent_care', billingModel: 'referral',
    patientCode: '', staffCode: '', emailDomains: '', locationName: '',
    entitlementTier: '', seatsPurchased: '', billingEmail: '',
    stripeCouponId: '', seatPriceId: '',
  });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setOrgs(await adminFetch('/orgs'));
    } catch (err) {
      if (/Unauthorized|Forbidden|expired/.test(err.message)) { clearToken(); onLogout(); }
      else setError(err.message);
    } finally { setLoading(false); }
  }, [onLogout]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Changing the model changes what a seat is worth. Moving onto facility_paid
  // pre-fills the grant, because a facility invoiced per seat whose staff get
  // nothing is the one combination that is never intended. Moving off it clears
  // the grant: referral is attribution only, and employee_paid already hands the
  // staff member a coupon, so a grant on top would be a double benefit.
  const setBillingModel = (e) => {
    const billingModel = e.target.value;
    setForm((f) => ({
      ...f,
      billingModel,
      entitlementTier: billingModel === 'facility_paid' ? (f.entitlementTier || 'active') : '',
    }));
  };

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setError('');
    try {
      await adminFetch('/orgs', { method: 'POST', body: JSON.stringify(form) });
      setForm((f) => ({ ...f, name: '', patientCode: '', staffCode: '', emailDomains: '', locationName: '' }));
      setCreating(false);
      load();
    } catch (err) { setError(err.message); }
  };

  const patch = async (id, data) => {
    try { await adminFetch(`/orgs/${id}`, { method: 'PATCH', body: JSON.stringify(data) }); load(); }
    catch (err) { setError(err.message); }
  };

  // Contract terms change after a facility is signed — a referral pilot becomes
  // a paid one, a seat cap moves, a coupon is renegotiated. Without this the
  // only way to alter any of it was a hand-written PATCH.
  const [editing, setEditing] = useState(null);
  const [edit, setEdit] = useState({});
  const setE = (k) => (e) => setEdit((f) => ({ ...f, [k]: e.target.value }));

  const openEdit = (o) => {
    setEditing(o.id);
    setEdit({
      billingModel: o.billingModel,
      entitlementTier: o.entitlementTier || '',
      seatPriceId: o.seatPriceId || '',
      seatsPurchased: o.seatsPurchased ?? '',
      billingEmail: o.billingEmail || '',
      stripeCouponId: o.stripeCouponId || '',
    });
  };

  // Mirrors the create form: moving onto per-seat billing pre-fills the grant,
  // moving off it clears the grant. The server applies the same rule, so a
  // facility cannot be left billed for seats that give nothing either way.
  const setEditModel = (e) => {
    const billingModel = e.target.value;
    setEdit((f) => ({
      ...f,
      billingModel,
      entitlementTier: billingModel === 'facility_paid' ? (f.entitlementTier || 'active') : '',
    }));
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    await patch(editing, edit);
    setEditing(null);
  };

  // What a facility is shown about itself. Read from OrgMetricDaily only, which
  // is the rule org-facing endpoints live under — so this panel is the same
  // view a partner would get, seen from our side of the table.
  const [reporting, setReporting] = useState(null);
  const [report, setReport] = useState(null);

  const [links, setLinks] = useState([]);
  const [minted, setMinted] = useState(null); // shown once; only a digest is stored

  const openReport = async (o) => {
    if (reporting === o.id) { setReporting(null); return; }
    setReporting(o.id); setReport(null); setMinted(null);
    try {
      const [m, l] = await Promise.all([
        adminFetch(`/orgs/${o.id}/metrics`),
        adminFetch(`/orgs/${o.id}/report-links`),
      ]);
      setReport(m); setLinks(l);
    } catch (err) { setError(err.message); setReporting(null); }
  };

  // The token comes back exactly once. If it is lost, the link is replaced
  // rather than recovered — which is the same property that makes a leaked
  // database row worthless.
  const mintLink = async (o) => {
    const who = window.prompt('Your first and last name, for the audit log');
    if (!who?.trim()) return;
    const label = window.prompt('Who is this link for? (e.g. "Dana, practice manager")') || '';
    const [first, ...rest] = who.trim().split(' ');
    try {
      const created = await adminFetch(`/orgs/${o.id}/report-link`, {
        method: 'POST',
        body: JSON.stringify({
          adminFirstName: first, adminLastName: rest.join(' ') || '-',
          reason: `Report link for ${label || o.name}`, label, days: 90,
        }),
      });
      setMinted(created);
      setLinks(await adminFetch(`/orgs/${o.id}/report-links`));
    } catch (err) { setError(err.message); }
  };

  const revokeLink = async (o, id) => {
    const who = window.prompt('Your first and last name, for the audit log');
    if (!who?.trim()) return;
    const [first, ...rest] = who.trim().split(' ');
    try {
      await adminFetch(`/report-links/${id}/revoke`, {
        method: 'POST',
        body: JSON.stringify({ adminFirstName: first, adminLastName: rest.join(' ') || '-',
                               reason: 'Revoked from admin portal' }),
      });
      setLinks(await adminFetch(`/orgs/${o.id}/report-links`));
    } catch (err) { setError(err.message); }
  };
  const toggleCode = async (id, active) => {
    try { await adminFetch(`/codes/${id}`, { method: 'PATCH', body: JSON.stringify({ active }) }); load(); }
    catch (err) { setError(err.message); }
  };

  const card = dark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const label = dark ? 'text-slate-400' : 'text-slate-500';
  const value = dark ? 'text-slate-100' : 'text-slate-800';
  const input = `w-full rounded-lg px-3 py-2 text-sm border ${dark ? 'bg-slate-900 border-slate-600 text-slate-100' : 'bg-white border-slate-300 text-slate-800'} focus:outline-none focus:ring-2 focus:ring-cyan-500`;

  const MODEL_COPY = {
    referral: 'Patient cards only — nothing billed, nothing granted',
    employee_paid: 'Staff subscribe themselves at the coupon rate',
    facility_paid: 'Facility invoiced per seat; staff pay nothing',
  };

  return (
    <div>
      {error && <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 text-sm">{error}</div>}

      <div className="flex justify-between items-center mb-4">
        <p className={`text-sm ${label}`}>{orgs.length} facilit{orgs.length === 1 ? 'y' : 'ies'}</p>
        <button onClick={() => setCreating((c) => !c)}
          className="bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cyan-700 transition-colors">
          {creating ? 'Cancel' : '+ New facility'}
        </button>
      </div>

      {creating && (
        <form onSubmit={create} className={`border rounded-xl p-5 mb-6 ${card}`}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={`block text-xs font-semibold mb-1 ${label}`}>Facility name</label>
              <input className={input} value={form.name} onChange={set('name')} placeholder="Riverside Urgent Care" />
            </div>
            <div>
              <label className={`block text-xs font-semibold mb-1 ${label}`}>Type</label>
              <select className={input} value={form.type} onChange={set('type')}>
                {['urgent_care', 'primary_care', 'dental', 'fqhc', 'dialysis', 'employer', 'union'].map(t =>
                  <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className={`block text-xs font-semibold mb-1 ${label}`}>Billing model</label>
              <select className={input} value={form.billingModel} onChange={setBillingModel}>
                <option value="referral">referral</option>
                <option value="employee_paid">employee_paid</option>
                <option value="facility_paid">facility_paid</option>
              </select>
            </div>
            <p className={`sm:col-span-2 text-xs ${label}`}>{MODEL_COPY[form.billingModel]}</p>

            <div>
              <label className={`block text-xs font-semibold mb-1 ${label}`}>Patient card code</label>
              <input className={input} value={form.patientCode} onChange={set('patientCode')} placeholder="RIVERSIDE" />
            </div>
            <div>
              <label className={`block text-xs font-semibold mb-1 ${label}`}>Location (optional)</label>
              <input className={input} value={form.locationName} onChange={set('locationName')} placeholder="Main St" />
            </div>
            <div>
              <label className={`block text-xs font-semibold mb-1 ${label}`}>Staff code</label>
              <input className={input} value={form.staffCode} onChange={set('staffCode')} placeholder="RIVERSIDESTAFF" />
            </div>
            <div>
              <label className={`block text-xs font-semibold mb-1 ${label}`}>Staff email domains</label>
              <input className={input} value={form.emailDomains} onChange={set('emailDomains')} placeholder="riversideuc.com" />
            </div>
            {form.staffCode && !form.emailDomains && (
              <p className="sm:col-span-2 text-xs text-amber-600">
                Without domains, anyone who sees the staff code can claim a seat — and under facility_paid that is a line on the invoice.
              </p>
            )}

            {form.billingModel === 'employee_paid' && (
              <div className="sm:col-span-2">
                <label className={`block text-xs font-semibold mb-1 ${label}`}>Stripe coupon ID</label>
                <input className={input} value={form.stripeCouponId} onChange={set('stripeCouponId')} placeholder="staff_52_off" />
              </div>
            )}
            {form.billingModel === 'facility_paid' && (<>
              <div className="sm:col-span-2">
                <label className={`block text-xs font-semibold mb-1 ${label}`}>Staff access</label>
                <select className={input} value={form.entitlementTier} onChange={set('entitlementTier')}>
                  <option value="active">Full access</option>
                  <option value="starter">Basic</option>
                </select>
                <p className={`mt-1 text-xs ${label}`}>
                  What an occupied seat grants. Open-ended on purpose — the seat is billed
                  every month, so access should last as long as the facility keeps paying for it.
                </p>
              </div>
              <div>
                <label className={`block text-xs font-semibold mb-1 ${label}`}>Seat price ID</label>
                <input className={input} value={form.seatPriceId} onChange={set('seatPriceId')} placeholder="price_1ABC…" />
              </div>
              <div>
                <label className={`block text-xs font-semibold mb-1 ${label}`}>Seat cap</label>
                <input className={input} value={form.seatsPurchased} onChange={set('seatsPurchased')} placeholder="40" />
              </div>
              <div className="sm:col-span-2">
                <label className={`block text-xs font-semibold mb-1 ${label}`}>Billing email</label>
                <input className={input} value={form.billingEmail} onChange={set('billingEmail')} placeholder="ap@riversideuc.com" />
              </div>
              <p className={`sm:col-span-2 text-xs ${label}`}>
                Creating the facility does not start billing. Run <code>npm run facility-billing</code> to open the Stripe subscription.
              </p>
            </>)}
          </div>
          <button type="submit" className="mt-4 bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cyan-700">
            Create facility
          </button>
        </form>
      )}

      {loading ? <p className={`text-sm ${label}`}>Loading…</p> : orgs.length === 0 ? (
        <p className={`text-sm ${label}`}>No facilities yet.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {orgs.map((o) => (
            <div key={o.id} className={`border rounded-xl p-5 ${card}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className={`font-semibold ${value}`}>{o.name}</p>
                  <p className={`text-xs mt-0.5 ${label}`}>
                    {o.type.replace(/_/g, ' ')} · {o.billingModel}
                    {o.status !== 'active' && <span className="text-amber-600 font-semibold"> · {o.status}</span>}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openReport(o)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${dark ? 'border-slate-600 text-slate-300' : 'border-slate-300 text-slate-600'}`}>
                    {reporting === o.id ? 'Hide report' : 'Reporting'}
                  </button>
                  <button onClick={() => (editing === o.id ? setEditing(null) : openEdit(o))}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${dark ? 'border-slate-600 text-slate-300' : 'border-slate-300 text-slate-600'}`}>
                    {editing === o.id ? 'Cancel' : 'Edit'}
                  </button>
                  <button onClick={() => patch(o.id, { status: o.status === 'active' ? 'paused' : 'active' })}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${dark ? 'border-slate-600 text-slate-300' : 'border-slate-300 text-slate-600'}`}>
                    {o.status === 'active' ? 'Pause' : 'Resume'}
                  </button>
                </div>
              </div>

              {reporting === o.id && (
                <div className={`mt-4 pt-4 border-t ${dark ? 'border-slate-700' : 'border-slate-200'}`}>
                  {!report ? <p className={`text-sm ${label}`}>Loading…</p> : (
                    <>
                      <div className="grid gap-4 sm:grid-cols-2">
                        {['patient', 'staff'].map((cohort) => {
                          const t = report.totals?.[cohort];
                          if (!t) return null;
                          return (
                            <div key={cohort} className={`rounded-lg p-3 ${dark ? 'bg-slate-900/50' : 'bg-slate-50'}`}>
                              <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${label}`}>{cohort}s</p>
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  ['Codes redeemed', t.redemptions],
                                  ['Reports explained', t.partial ? `${t.reportsAnalyzed}+` : t.reportsAnalyzed],
                                  ['Busiest day', t.peakDailyActiveUsers],
                                  ['Understanding', t.avgComprehension === null ? '—' : `${t.avgComprehension}%`],
                                ].map(([l, v]) => (
                                  <div key={l}>
                                    <p className={`text-xs ${label}`}>{l}</p>
                                    <p className={`text-base font-bold tabular-nums ${value}`}>{v}</p>
                                  </div>
                                ))}
                              </div>
                              {t.partial && (
                                <p className={`mt-2 text-xs ${label}`}>
                                  {t.suppressedDays} day{t.suppressedDays === 1 ? '' : 's'} withheld —
                                  too few people to report without identifying them. A floor, not a total.
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {report.series?.length === 0 && (
                        <p className={`mt-3 text-xs ${label}`}>
                          Nothing rolled up yet. The nightly job writes yesterday at 01:15 UTC.
                        </p>
                      )}
                      <p className={`mt-3 text-xs ${label}`}>
                        Counts only — never who, and never what any result said.
                      </p>

                      <div className={`mt-4 pt-4 border-t ${dark ? 'border-slate-700' : 'border-slate-200'}`}>
                        <div className="flex items-center justify-between gap-3">
                          <p className={`text-xs font-semibold uppercase tracking-wide ${label}`}>Shareable links</p>
                          <button onClick={() => mintLink(o)}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-cyan-600 text-white hover:bg-cyan-700">
                            + New link
                          </button>
                        </div>

                        {minted && (
                          <div className="mt-3 rounded-lg border border-cyan-300 bg-cyan-50 p-3">
                            <p className="text-xs font-semibold text-cyan-900">
                              Copy this now — it is shown once and cannot be retrieved.
                            </p>
                            <code className="block mt-1 text-xs break-all text-cyan-900">{minted.url}</code>
                            <button onClick={() => navigator.clipboard?.writeText(minted.url)}
                              className="mt-2 text-xs font-semibold text-cyan-800 underline underline-offset-2">
                              Copy link
                            </button>
                          </div>
                        )}

                        {links.length === 0 ? (
                          <p className={`mt-2 text-xs ${label}`}>None yet. A link lets this facility see the figures above without an account.</p>
                        ) : (
                          <div className="mt-2 flex flex-col gap-1.5">
                            {links.map((l) => {
                              const dead = l.revokedAt || new Date(l.expiresAt) <= new Date();
                              return (
                                <div key={l.id} className="flex flex-wrap items-center gap-2 text-xs">
                                  <span className={dead ? label : value}>{l.label || 'unlabelled'}</span>
                                  <span className={label}>
                                    {l.revokedAt ? 'revoked'
                                      : new Date(l.expiresAt) <= new Date() ? 'expired'
                                      : `until ${new Date(l.expiresAt).toLocaleDateString()}`}
                                  </span>
                                  <span className={label}>· {l.viewCount} view{l.viewCount === 1 ? '' : 's'}</span>
                                  {!dead && (
                                    <button onClick={() => revokeLink(o, l.id)}
                                      className="ml-auto font-semibold text-amber-600 hover:text-amber-700">
                                      Revoke
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {editing === o.id && (
                <form onSubmit={saveEdit} className={`mt-4 pt-4 border-t ${dark ? 'border-slate-700' : 'border-slate-200'}`}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className={`block text-xs font-semibold mb-1 ${label}`}>Billing model</label>
                      <select className={input} value={edit.billingModel} onChange={setEditModel}>
                        <option value="referral">referral</option>
                        <option value="employee_paid">employee_paid</option>
                        <option value="facility_paid">facility_paid</option>
                      </select>
                    </div>
                    <p className={`self-end text-xs pb-2 ${label}`}>{MODEL_COPY[edit.billingModel]}</p>

                    {edit.billingModel === 'employee_paid' && (
                      <div className="sm:col-span-2">
                        <label className={`block text-xs font-semibold mb-1 ${label}`}>Stripe coupon ID</label>
                        <input className={input} value={edit.stripeCouponId} onChange={setE('stripeCouponId')} placeholder="staff_52_off" />
                      </div>
                    )}
                    {edit.billingModel === 'facility_paid' && (<>
                      <div className="sm:col-span-2">
                        <label className={`block text-xs font-semibold mb-1 ${label}`}>Staff access</label>
                        <select className={input} value={edit.entitlementTier} onChange={setE('entitlementTier')}>
                          <option value="active">Full access</option>
                          <option value="starter">Basic</option>
                        </select>
                      </div>
                      <div>
                        <label className={`block text-xs font-semibold mb-1 ${label}`}>Seat price ID</label>
                        <input className={input} value={edit.seatPriceId} onChange={setE('seatPriceId')} placeholder="price_1ABC…" />
                      </div>
                      <div>
                        <label className={`block text-xs font-semibold mb-1 ${label}`}>Seat cap</label>
                        <input className={input} value={edit.seatsPurchased} onChange={setE('seatsPurchased')} placeholder="40" />
                      </div>
                      <div className="sm:col-span-2">
                        <label className={`block text-xs font-semibold mb-1 ${label}`}>Billing email</label>
                        <input className={input} value={edit.billingEmail} onChange={setE('billingEmail')} placeholder="ap@riversideuc.com" />
                      </div>
                      <p className={`sm:col-span-2 text-xs ${label}`}>
                        Changing the model does not open or close a Stripe subscription. Run <code>npm run facility-billing</code> to start
                        seat billing, and <code>npm run sync-seats</code> to bring the quantity in line with the seats already taken.
                      </p>
                    </>)}
                  </div>
                  <button type="submit" className="mt-4 bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cyan-700">
                    Save changes
                  </button>
                </form>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                {[
                  ['Staff seats', o.billingModel === 'facility_paid' && o.seatsPurchased ? `${o.seatsInUse} / ${o.seatsPurchased}` : o.seatsInUse],
                  ['Patients attributed', o.patientsAttributed],
                  ['Locations', o.locations.length],
                  ['Codes', o.codes.length],
                ].map(([l, v]) => (
                  <div key={l}>
                    <p className={`text-xs ${label}`}>{l}</p>
                    <p className={`text-lg font-bold tabular-nums ${value}`}>{v}</p>
                  </div>
                ))}
              </div>

              <div className={`mt-4 pt-4 border-t ${dark ? 'border-slate-700' : 'border-slate-200'} flex flex-col gap-2`}>
                {o.codes.map((c) => (
                  <div key={c.id} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className={`font-mono font-semibold ${value}`}>{c.code}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${c.kind === 'staff'
                      ? 'bg-indigo-50 text-indigo-700' : 'bg-cyan-50 text-cyan-700'}`}>{c.kind}</span>
                    {c.kind === 'staff' && (
                      <span className={`text-xs ${c.emailDomains.length ? label : 'text-amber-600 font-semibold'}`}>
                        {c.emailDomains.length ? `@${c.emailDomains.join(' @')}` : 'unrestricted'}
                      </span>
                    )}
                    <span className={`text-xs tabular-nums ${label}`}>{c.redemptions} redeemed</span>
                    <button onClick={() => toggleCode(c.id, !c.active)}
                      className={`ml-auto text-xs font-semibold px-2.5 py-1 rounded-md border ${dark ? 'border-slate-600' : 'border-slate-300'} ${c.active ? 'text-slate-500' : 'text-emerald-600'}`}>
                      {c.active ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                ))}
                <p className={`text-xs mt-1 ${label}`}>
                  Card URL: <code>https://healthclearai.ai/?c={o.codes.find(c => c.kind === 'patient')?.code || '…'}</code>
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(() => !!getToken());

  return authed
    ? <AdminDashboard onLogout={() => setAuthed(false)} />
    : <AdminLogin onLogin={() => setAuthed(true)} />;
}
